import React, { useEffect, useState } from 'react';
import { Form, Input, Button, Image, TimePicker, Select, InputNumber, Upload, message, Card, Spin, Alert } from 'antd';
import { CheckCircleOutlined, CloseCircleOutlined, UploadOutlined } from '@ant-design/icons';
import { useDispatch, useSelector } from 'react-redux';
import { createSubService, getSubServices, updateServiceQty } from '../../../store/slices/subServiceSlice';
import { useLocation } from 'react-router-dom';
import TableComponent from '../../../components/TableComponent';
import noImage from '../../../assets/images/no-image.png';

const AddSubService = () => {
    const [form] = Form.useForm();
    const [qtyForm] = Form.useForm();
    const dispatch = useDispatch()
    const [reload, setReload] = useState(false)
    const [buttonLoading, setButtonLoading] = useState(false);
    const [buttonQtyLoading, setButtonQtyLoading] = useState(false)
    const [alertMessage, setAlertMessage] = useState("");
    const [alertType, setAlertType] = useState("success");
    const [alertQtyMessage, setAlertQtyMessage] = useState("");
    const [alertQtyType, setAlertQtyType] = useState("success");
    const [fileList, setFileList] = useState([]);

    const { state } = useLocation()

    const searchData = {
        serviceId : state?.id
    }

    const handleChange = (info) => {
        setFileList(info.fileList.slice(-1));
    };

    const beforeUpload = (file) => {
        const isImage = file.type.startsWith('image/');
        if (!isImage) {
            message.error('You can only upload image files!');
            return Upload.LIST_IGNORE;
        }
        return false; // prevent auto upload
    };

    const handleFinish = async (values) => {
        setButtonLoading(true);
        setAlertMessage("");
        try {
            await new Promise(resolve => setTimeout(resolve, 1000));
            const formData = new FormData()
            formData.append('name', values?.name);
            formData.append('serviceId', state?.id)
            formData.append('cost', values?.cost)
            if (values?.image && values.image.file) {
                formData.append('image', values?.image?.file);
            }
            const resultAction = await dispatch(createSubService(formData))
            if (createSubService.fulfilled.match(resultAction)) {
                setAlertMessage(resultAction?.payload?.message || 'Sub service created successfully');
                setAlertType('success');
                form.resetFields();
                setFileList([]);
                setReload(!reload)
            } else {
                console.log(resultAction?.payload)
                setAlertMessage(resultAction?.payload?.error || 'Error creating sub service');
                setAlertType('error');
            }
        } catch (error) {
            setAlertMessage('Failed to create sub service.');
            setAlertType('error');
        } finally {
            setButtonLoading(false);
            setTimeout(() => {
                setAlertMessage('');
            }, 4000);
        }
    };

    const columns = [
        {
            title: 'Image',
            dataIndex: 'image',
            key: 'image',
            width: 120,
            render: (image, record) => {
                const imageSrc = image && typeof image === 'string' && image.trim() !== '' ? image : noImage;
                return (
                    <Image
                        src={imageSrc}
                        alt="Sub Service"
                        width={80}
                        height={80}
                        style={{ objectFit: 'cover', borderRadius: 4 }}
                        fallback={noImage}
                        preview={false}
                        onError={(e) => {
                            console.error('Image load error for:', imageSrc, e);
                            e.target.src = noImage;
                        }}
                    />
                );
            },
        },
        {
            title: 'Name',
            dataIndex: 'name',
            key: 'name',
        },
        {
            title: 'Cost',
            dataIndex: 'cost',
            key: 'cost',
        }
    ];

    const handleUpdateQty = async (values) => {
        setButtonQtyLoading(true);
        setAlertQtyMessage("");
        try {
                await new Promise(resolve => setTimeout(resolve, 1000));
                const data = {
                    quantity : values?.quantity,
                    serviceId: state?.id
                }
                const resultAction = await dispatch(updateServiceQty(data))
                if (updateServiceQty.fulfilled.match(resultAction)) {
                    setAlertQtyMessage(resultAction?.payload?.message || 'Quantity updated successfully');
                    setAlertQtyType('success');
                    qtyForm.resetFields();
                } else {
                    console.log(resultAction?.payload)
                    setAlertQtyMessage(resultAction?.payload?.error || 'Error updating quantity');
                    setAlertQtyType('error');
                }
            } catch (error) {
                setAlertQtyMessage('Failed to update quantity.');
                setAlertQtyType('error');
            } finally {
                setButtonQtyLoading(false);
                setTimeout(() => {
                    setAlertQtyMessage('');
                }, 4000);
            }
    }

    return (
        <>
            <Card title="Create Sub Service" bordered={false}>
                {alertMessage && (
                    <Alert
                        message={alertMessage}
                        type={alertType}
                        showIcon
                        icon={alertType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        closable
                        onClose={() => setAlertMessage('')}
                        style={{
                            marginBottom: 24,
                            borderRadius: '8px',
                        }}
                    />
                )}
                <Form
                    layout="vertical"
                    form={form}
                    onFinish={handleFinish}
                >

                    <Form.Item
                        label="Sub Service Name"
                        name="name"
                        rules={[{ required: true, message: 'Please enter sub service name' }]}
                    >
                        <Input placeholder="Enter sub service name" rows={4} />
                    </Form.Item>

                    <Form.Item
                        label="Cost"
                        name="cost"
                        rules={[{ required: true, message: 'Please enter cost' }]}
                    >
                        <InputNumber placeholder="Enter cost" style={{ width : "100%" }} />
                    </Form.Item>

                    <Form.Item label="Image" name="image"  rules={[{ required: true, message: 'Please upload image' }]}>
                        <Upload
                        beforeUpload={beforeUpload}
                        fileList={fileList}
                        onChange={handleChange}
                        maxCount={1}
                        accept="image/*"
                        listType="picture"
                        >
                        <Button icon={<UploadOutlined />}>Select Image</Button>
                        </Upload>
                    </Form.Item>

                    <Form.Item>
                        <Button
                        type="primary"
                        htmlType="submit"
                        loading={buttonLoading}
                        style={{ marginTop: 16 }}
                        >
                        Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
            <Card title="Update Quantity" style={{ marginTop: 20 }}>
                {alertQtyMessage && (
                    <Alert
                        message={alertQtyMessage}
                        type={alertQtyType}
                        showIcon
                        icon={alertQtyType === 'success' ? <CheckCircleOutlined /> : <CloseCircleOutlined />}
                        closable
                        onClose={() => setAlertQtyMessage('')}
                        style={{
                            marginBottom: 24,
                            borderRadius: '8px',
                        }}
                    />
                )}
                <Form
                    layout="vertical"
                    form={qtyForm}
                    onFinish={handleUpdateQty}
                >

                    <Form.Item
                        label="Quantity"
                        name="quantity"
                        rules={[{ required: true, message: 'Please enter quantity' }]}
                    >
                        <Input placeholder="Enter quantity" rows={4} />
                    </Form.Item>

                    <Form.Item>
                        <Button
                        type="primary"
                        htmlType="submit"
                        loading={buttonQtyLoading}
                        style={{ marginTop: 16 }}
                        >
                        Submit
                        </Button>
                    </Form.Item>
                </Form>
            </Card>
            <Card title="Sub Services" style={{ marginTop: 20 }}>
                <TableComponent
                    listData={getSubServices}
                    columns={columns}
                    placeholder="Search sub services..."
                    reload={reload}
                    searchData={searchData}
                />
            </Card>
        </>
    );
};

export default AddSubService;